import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { getEffectiveUserId } from '../auth/current-user-identity';
import { MeasurementProfile, Fitting, CostumePiece, QuickChangeTransition, CostumeSizingProfile } from './types';
import type { QuickChangeCandidate } from '../personnel/hooks/useDoublingData';
import { createLastModifiedBy } from '../../utils/lastModifiedBy';
import type { ProjectScope } from '../sync/project-scope';
import {
    deleteCostumePiece,
    deleteCostumeSizingProfile,
    deleteFitting as deleteFittingFromCloud,
    deleteMeasurementProfile,
    deleteQuickChangeTransition,
    saveCostumePiece,
    saveCostumeSizingProfile,
    saveFitting,
    saveMeasurementProfile,
    saveOwnCostumeSizingProfile,
    saveQuickChangeTransition,
} from './costumes-api';
import {
    markPendingCostumeFittingDelete,
    markPendingCostumeFittingWrite,
    markPendingCostumeMeasurementDelete,
    markPendingCostumeMeasurementWrite,
    markPendingCostumePieceDelete,
    markPendingCostumePieceWrite,
    markPendingCostumeQuickChangeDelete,
    markPendingCostumeQuickChangeWrite,
    markPendingCostumeSizingDelete,
    markPendingCostumeSizingWrite,
} from '../sync/costume-pending-writes';
import { logger } from '../../utils/logger';

type CostumeWriteContext = {
    scope: ProjectScope;
    actorId: string;
};

type CostumeSizingProfileWriter = (
    scope: ProjectScope,
    profile: CostumeSizingProfile,
    options: { actorId?: string }
) => Promise<void>;

function cloudCostumeWriteContext(state: AppState & CostumesSlice): CostumeWriteContext | null {
    const actorId = state.auth.user ? getEffectiveUserId() : undefined;
    if (!state.cloud.enabled || !state.cloud.orgId || !state.cloud.projectId || !actorId) {
        return null;
    }

    return {
        scope: {
            orgId: state.cloud.orgId,
            projectId: state.cloud.projectId,
        },
        actorId,
    };
}

function preserveCostumeWrite<T>(
    operation: Promise<T>,
    label: string
): void {
    void operation.catch((error) => {
        logger.error(`[costumes-store] Failed to preserve ${label}:`, error);
    });
}

function sanitizedSizingOnlyMeasurement(measurement: MeasurementProfile): MeasurementProfile {
    return {
        ...measurement,
        measurements: {},
        measurementNotes: {},
        allergies: [],
        notes: '',
        isComplete: false,
        completedBy: undefined,
        completedAt: undefined,
    };
}

function sizingProfileFromMeasurement(measurement: MeasurementProfile): CostumeSizingProfile {
    const profile: CostumeSizingProfile = {
        actorId: measurement.actorId,
        sizing: measurement.sizing,
        lastUpdated: measurement.lastUpdated,
    };
    if (measurement.appearance !== undefined) {
        profile.appearance = measurement.appearance;
    }
    return profile;
}

function preserveSizingProfileWrite(
    writeContext: CostumeWriteContext | null,
    measurement: MeasurementProfile,
    writer: CostumeSizingProfileWriter,
    label: string
): void {
    if (!writeContext) {
        return;
    }

    const { scope, actorId: writeActorId } = writeContext;
    markPendingCostumeSizingWrite(scope, measurement.actorId);
    preserveCostumeWrite(
        writer(scope, sizingProfileFromMeasurement(measurement), { actorId: writeActorId }),
        label
    );
}

function quickChangeTransitionFromCandidate(
    id: string,
    candidate: QuickChangeCandidate,
    status: QuickChangeTransition['status'],
    lastModifiedBy?: QuickChangeTransition['lastModifiedBy']
): QuickChangeTransition {
    const transition: QuickChangeTransition = {
        id,
        personnelId: candidate.personnelId,
        exitSceneId: candidate.exitSceneId,
        exitCharacterId: candidate.exitCharacterId,
        enterSceneId: candidate.enterSceneId,
        enterCharacterId: candidate.enterCharacterId,
        status,
    };

    if (lastModifiedBy) {
        transition.lastModifiedBy = lastModifiedBy;
    }

    return transition;
}

function preserveQuickChangeTransitionWrite(
    writeContext: CostumeWriteContext | null,
    transition: QuickChangeTransition
): void {
    if (!writeContext) {
        return;
    }

    const { scope, actorId: writeActorId } = writeContext;
    markPendingCostumeQuickChangeWrite(scope, transition.id);
    preserveCostumeWrite(
        saveQuickChangeTransition(scope, transition, { actorId: writeActorId }),
        'costume quick change'
    );
}

function upsertSizingOnlyMeasurement(
    measurements: MeasurementProfile[],
    measurement: MeasurementProfile
): MeasurementProfile[] {
    const sanitizedMeasurement = sanitizedSizingOnlyMeasurement(measurement);
    const appearanceUpdate = sanitizedMeasurement.appearance !== undefined
        ? { appearance: sanitizedMeasurement.appearance }
        : {};
    const existing = measurements.findIndex(m => m.actorId === measurement.actorId);

    if (existing < 0) {
        return [...measurements, sanitizedMeasurement];
    }

    const existingProfile = measurements[existing];
    if (!existingProfile) {
        return measurements;
    }

    return measurements.map((profile, index) => (
        index === existing
            ? {
                ...existingProfile,
                silhouette: sanitizedMeasurement.silhouette,
                unitPreference: existingProfile.unitPreference || sanitizedMeasurement.unitPreference,
                measurementDate: existingProfile.measurementDate || sanitizedMeasurement.measurementDate,
                sizing: sanitizedMeasurement.sizing,
                ...appearanceUpdate,
                lastUpdated: sanitizedMeasurement.lastUpdated,
            }
            : profile
    ));
}

export interface CostumesSlice {
    costumes: {
        measurements: MeasurementProfile[];
        fittings: Fitting[];
        pieces: CostumePiece[];
        quickChangeTransitions: QuickChangeTransition[];
    };
    costumesActions: {
        // Measurements
        addOrUpdateMeasurement: (measurement: MeasurementProfile) => void;
        addOrUpdateCostumeSizing: (measurement: MeasurementProfile) => void;
        addOrUpdateSelfReportedSizing: (measurement: MeasurementProfile) => void;
        deleteMeasurement: (actorId: string) => void;
        
        // Fittings
        addFitting: (fitting: Partial<Fitting>) => string;
        updateFitting: (id: string, updates: Partial<Fitting>) => void;
        deleteFitting: (id: string) => void;
        
        // Costume Pieces
        addCostumePiece: (piece: Partial<CostumePiece>) => string;
        updateCostumePiece: (id: string, updates: Partial<CostumePiece>) => void;
        deleteCostumePieces: (ids: string[]) => void;
        
        // Quick Change Transitions
        confirmTransition: (candidate: QuickChangeCandidate) => string;
        dismissTransition: (candidate: QuickChangeCandidate) => string;
        updateTransition: (id: string, updates: Partial<QuickChangeTransition>) => void;
        removeTransition: (id: string) => void;
        
        // Project scoping
        clearCostumes: () => void;
    };
}

export const createCostumesSlice: StateCreator<
    AppState & CostumesSlice,
    [["zustand/immer", never]],
    [],
    CostumesSlice
> = (set, get) => ({
    costumes: {
        measurements: [],
        fittings: [],
        pieces: [],
        quickChangeTransitions: [],
    },
    costumesActions: {
        // Measurements
        addOrUpdateMeasurement: (measurement) => {
            set(state => {
                if (!state.costumes) {
                    state.costumes = { measurements: [], fittings: [], pieces: [], quickChangeTransitions: [] };
                }
                const existing = state.costumes.measurements.findIndex(
                    m => m.actorId === measurement.actorId
                );
                if (existing >= 0) {
                    state.costumes.measurements[existing] = measurement;
                } else {
                    state.costumes.measurements.push(measurement);
                }
            });
            const writeContext = cloudCostumeWriteContext(get());
            if (writeContext) {
                const { scope, actorId: writeActorId } = writeContext;
                markPendingCostumeMeasurementWrite(scope, measurement.actorId);
                preserveCostumeWrite(saveMeasurementProfile(scope, measurement, { actorId: writeActorId }), 'costume measurement');
                preserveSizingProfileWrite(writeContext, measurement, saveCostumeSizingProfile, 'costume sizing');
            }
        },
        addOrUpdateSelfReportedSizing: (measurement) => {
            set(state => {
                if (!state.costumes) {
                    state.costumes = { measurements: [], fittings: [], pieces: [], quickChangeTransitions: [] };
                }
                state.costumes.measurements = upsertSizingOnlyMeasurement(state.costumes.measurements, measurement);
            });
            preserveSizingProfileWrite(
                cloudCostumeWriteContext(get()),
                measurement,
                saveOwnCostumeSizingProfile,
                'costume sizing self-report'
            );
        },
        addOrUpdateCostumeSizing: (measurement) => {
            set(state => {
                if (!state.costumes) {
                    state.costumes = { measurements: [], fittings: [], pieces: [], quickChangeTransitions: [] };
                }
                state.costumes.measurements = upsertSizingOnlyMeasurement(state.costumes.measurements, measurement);
            });
            preserveSizingProfileWrite(
                cloudCostumeWriteContext(get()),
                measurement,
                saveCostumeSizingProfile,
                'costume sizing'
            );
        },
        deleteMeasurement: (actorId) => {
            set(state => {
                if (!state.costumes) return;
                state.costumes.measurements = state.costumes.measurements.filter(
                    m => m.actorId !== actorId
                );
            });
            const writeContext = cloudCostumeWriteContext(get());
            if (writeContext) {
                const { scope, actorId: writeActorId } = writeContext;
                markPendingCostumeMeasurementDelete(scope, actorId);
                markPendingCostumeSizingDelete(scope, actorId);
                preserveCostumeWrite(
                    deleteMeasurementProfile(scope, actorId, { actorId: writeActorId }),
                    'costume measurement delete'
                );
                preserveCostumeWrite(
                    deleteCostumeSizingProfile(scope, actorId, { actorId: writeActorId }),
                    'costume sizing delete'
                );
            }
        },
        
        // Fittings
        addFitting: (fitting) => {
            const id = crypto.randomUUID();
            const newFitting: Fitting = {
                actorId: fitting.actorId || '',
                date: fitting.date || new Date().toISOString().slice(0, 10),
                pieceIds: fitting.pieceIds || [],
                notes: fitting.notes || '',
                fittedBy: fitting.fittedBy || '',
                ...fitting,
                id,
            };
            set(state => {
                if (!state.costumes) {
                    state.costumes = { measurements: [], fittings: [], pieces: [], quickChangeTransitions: [] };
                }
                state.costumes.fittings.push(newFitting);
            });
            const writeContext = cloudCostumeWriteContext(get());
            if (writeContext) {
                const { scope, actorId: writeActorId } = writeContext;
                markPendingCostumeFittingWrite(scope, id);
                preserveCostumeWrite(saveFitting(scope, newFitting, { actorId: writeActorId }), 'costume fitting');
            }
            return id;
        },
        updateFitting: (id, updates) => {
            set(state => {
                if (!state.costumes) return;
                const fitting = state.costumes.fittings.find(f => f.id === id);
                if (fitting) {
                    Object.assign(fitting, updates);
                }
            });
            const writeContext = cloudCostumeWriteContext(get());
            const fitting = get().costumes.fittings.find(item => item.id === id);
            if (writeContext && fitting) {
                const { scope, actorId: writeActorId } = writeContext;
                markPendingCostumeFittingWrite(scope, id);
                preserveCostumeWrite(saveFitting(scope, fitting, { actorId: writeActorId }), 'costume fitting');
            }
        },
        deleteFitting: (id) => {
            set(state => {
                if (!state.costumes) return;
                state.costumes.fittings = state.costumes.fittings.filter(f => f.id !== id);
            });
            const writeContext = cloudCostumeWriteContext(get());
            if (writeContext) {
                const { scope, actorId: writeActorId } = writeContext;
                markPendingCostumeFittingDelete(scope, id);
                preserveCostumeWrite(deleteFittingFromCloud(scope, id, { actorId: writeActorId }), 'costume fitting delete');
            }
        },
        
        // Costume Pieces
        addCostumePiece: (piece) => {
            const id = crypto.randomUUID();
            const state = get();
            const lastModifiedBy = createLastModifiedBy(
                state.auth.user,
                state.cloud.member
            );
            const newPiece: CostumePiece = {
                sceneId: piece.sceneId || '',
                characterId: piece.characterId || '',
                description: piece.description || '',
                status: piece.status || 'undefined',
                images: piece.images || [],
                budget: piece.budget || { currency: 'USD' },
                maintenance: piece.maintenance || {
                    needsRepair: false,
                    repairNotes: '',
                    needsLaundry: false,
                    maintenanceNotes: '',
                },
                source: piece.source || { source: 'stock' },
                quickChange: piece.quickChange || { isQuickChange: false },
                notes: piece.notes || '',
                ...piece,
                id,
                lastModifiedBy
            };
            set(state => {
                if (!state.costumes) {
                    state.costumes = { measurements: [], fittings: [], pieces: [], quickChangeTransitions: [] };
                }
                state.costumes.pieces.push(newPiece);
            });
            const writeContext = cloudCostumeWriteContext(get());
            if (writeContext) {
                const { scope, actorId: writeActorId } = writeContext;
                markPendingCostumePieceWrite(scope, id);
                preserveCostumeWrite(saveCostumePiece(scope, newPiece, { actorId: writeActorId }), 'costume piece');
            }
            return id;
        },
        updateCostumePiece: (id, updates) => {
            set(state => {
                if (!state.costumes) return;
                const piece = state.costumes.pieces.find(p => p.id === id);
                if (piece) {
                    const lastModifiedBy = createLastModifiedBy(
                        state.auth.user,
                        state.cloud.member
                    );
                    Object.assign(piece, updates, { lastModifiedBy });
                }
            });
            const writeContext = cloudCostumeWriteContext(get());
            const piece = get().costumes.pieces.find(item => item.id === id);
            if (writeContext && piece) {
                const { scope, actorId: writeActorId } = writeContext;
                markPendingCostumePieceWrite(scope, id);
                preserveCostumeWrite(saveCostumePiece(scope, piece, { actorId: writeActorId }), 'costume piece');
            }
        },
        deleteCostumePieces: (ids) => {
            set(state => {
                if (!state.costumes) return;
                state.costumes.pieces = state.costumes.pieces.filter(p => !ids.includes(p.id));
            });
            const writeContext = cloudCostumeWriteContext(get());
            if (writeContext) {
                const { scope, actorId: writeActorId } = writeContext;
                for (const id of ids) {
                    markPendingCostumePieceDelete(scope, id);
                    preserveCostumeWrite(deleteCostumePiece(scope, id, { actorId: writeActorId }), 'costume piece delete');
                }
            }
        },

        // Quick Change Transitions
        confirmTransition: (candidate) => {
            const id = crypto.randomUUID();
            const state = get();
            const transition = quickChangeTransitionFromCandidate(
                id,
                candidate,
                'confirmed',
                createLastModifiedBy(state.auth.user, state.cloud.member)
            );
            set(state => {
                if (!state.costumes.quickChangeTransitions) {
                    state.costumes.quickChangeTransitions = [];
                }
                state.costumes.quickChangeTransitions.push(transition);
            });
            preserveQuickChangeTransitionWrite(cloudCostumeWriteContext(get()), transition);
            return id;
        },
        dismissTransition: (candidate) => {
            const id = crypto.randomUUID();
            const transition = quickChangeTransitionFromCandidate(
                id,
                candidate,
                'dismissed'
            );
            set(state => {
                if (!state.costumes.quickChangeTransitions) {
                    state.costumes.quickChangeTransitions = [];
                }
                state.costumes.quickChangeTransitions.push(transition);
            });
            preserveQuickChangeTransitionWrite(cloudCostumeWriteContext(get()), transition);
            return id;
        },
        updateTransition: (id, updates) => {
            set(state => {
                const t = state.costumes.quickChangeTransitions?.find(t => t.id === id);
                if (t) {
                    const lastModifiedBy = createLastModifiedBy(state.auth.user, state.cloud.member);
                    Object.assign(t, updates, { lastModifiedBy });
                }
            });
            const writeContext = cloudCostumeWriteContext(get());
            const transition = get().costumes.quickChangeTransitions.find(item => item.id === id);
            if (writeContext && transition) {
                const { scope, actorId: writeActorId } = writeContext;
                markPendingCostumeQuickChangeWrite(scope, id);
                preserveCostumeWrite(
                    saveQuickChangeTransition(scope, transition, { actorId: writeActorId }),
                    'costume quick change'
                );
            }
        },
        removeTransition: (id) => {
            set(state => {
                if (!state.costumes.quickChangeTransitions) return;
                state.costumes.quickChangeTransitions = state.costumes.quickChangeTransitions.filter(t => t.id !== id);
            });
            const writeContext = cloudCostumeWriteContext(get());
            if (writeContext) {
                const { scope, actorId: writeActorId } = writeContext;
                markPendingCostumeQuickChangeDelete(scope, id);
                preserveCostumeWrite(
                    deleteQuickChangeTransition(scope, id, { actorId: writeActorId }),
                    'costume quick change delete'
                );
            }
        },
        
        clearCostumes: () => set(state => {
            state.costumes = {
                measurements: [],
                fittings: [],
                pieces: [],
                quickChangeTransitions: [],
            };
        }),
    }
});









